import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "iconsax-react";

export default function VariantPricing({
  variants,
  onSpecs,
  onConfigure,
  title = "Choose Your Syros EV",
}) {
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [variants]);

  // Scroll by one card (card width + gap)
  const scrollByCard = (dir) => {
    const el = trackRef.current;
    const card = el?.firstElementChild;
    if (!el || !card) return;
    el.scrollBy({ left: dir * (card.offsetWidth + 24), behavior: "smooth" });
  };

  const arrowSx = {
    border: "1px solid #05141f",
    "&.Mui-disabled": { borderColor: "#ccc", opacity: 0.5 },
  };

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        fontFamily="KIAbodyLatinbasic_Bold"
        textAlign="center"
        sx={{ fontSize: "clamp(24px, 4vw, 32px)", mb: 4, px: 2 }}
      >
        {title}
      </Typography>

      <Box
        ref={trackRef}
        onScroll={updateArrows}
        sx={{
          display: "flex",
          gap: 3,
          overflowX: "auto",
          px: { xs: 2, md: 6 },
          pb: 2,
          scrollSnapType: "x mandatory",
          "&::-webkit-scrollbar": { height: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: "3px" },
        }}
      >
        {variants.map((variant, index) => (
          <Box
            key={index}
            sx={{
              flex: "0 0 auto",
              width: { xs: "82vw", sm: "320px" },
              scrollSnapAlign: "start",
              border: "1px solid #e5e5e5",
            }}
          >
            <Box sx={{ backgroundColor: "#05141f", color: "#fff", p: "20px 24px" }}>
              <Typography fontFamily="KIAbodyLatinbasic_Bold" sx={{ fontSize: "26px" }}>
                {variant.name}
              </Typography>
              <Typography fontFamily="KIAbodyLatinbasic_Regular" sx={{ fontSize: "18px" }}>
                ₹ {variant.price}{" "}
                <Box component="span" sx={{ color: "#FFFFFF99", fontSize: "12px" }}>
                  Starting MSRP*
                </Box>
              </Typography>
            </Box>

            <Box sx={{ p: "20px 24px" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: 3 }}>
                {variant.colors.map((color, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: color,
                      border: "1px solid lightgray",
                    }}
                  />
                ))}
              </Box>

              <Typography fontFamily="KIAbodyLatinbasic_Bold" sx={{ fontSize: "14px", mb: 1.5 }}>
                Features
              </Typography>
              {variant.features.map((feature, i) => (
                <Typography
                  key={i}
                  fontFamily="KIAbodyLatinbasic_Regular"
                  sx={{ fontSize: "13px", py: 0.6, lineHeight: 1.5 }}
                >
                  {feature}
                </Typography>
              ))}

              <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
                <Button
                  onClick={() => (onSpecs ? onSpecs(variant) : null)}
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#05141f",
                    borderRadius: "20px",
                    textTransform: "none",
                    fontSize: "13px",
                    "&:hover": { backgroundColor: "#000" },
                  }}
                >
                  Specs
                </Button>
                <Button
                  onClick={() => (onConfigure ? onConfigure(variant) : navigate("/enquiry"))}
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#05141f",
                    borderRadius: "20px",
                    textTransform: "none",
                    fontSize: "13px",
                    "&:hover": { backgroundColor: "#000" },
                  }}
                >
                  Configure
                </Button>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {(canPrev || canNext) && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
          <IconButton
            aria-label="Previous variant"
            onClick={() => scrollByCard(-1)}
            disabled={!canPrev}
            sx={arrowSx}
          >
            <ArrowLeft size={20} color={canPrev ? "#05141f" : "#ccc"} />
          </IconButton>
          <IconButton
            aria-label="Next variant"
            onClick={() => scrollByCard(1)}
            disabled={!canNext}
            sx={arrowSx}
          >
            <ArrowRight size={20} color={canNext ? "#05141f" : "#ccc"} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
